import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "nx_disclaimer_accepted_v1";

const ACKNOWLEDGEMENTS = [
  "The user wishes to access this website solely for obtaining information about Advocate Harshavardhan Prakash Patil and Nexus Legal, on his/her own initiative, without any solicitation, invitation, advertisement or inducement by the Advocate or the Firm.",
  "The information contained on this website is provided solely for general informational purposes and should not be construed as legal advice, legal opinion, or professional recommendation.",
  "Accessing this website, transmitting information through it, or communicating with the Advocate through this website shall not create an advocate-client relationship.",
  "No material available on this website constitutes legal advice. Users should seek independent legal advice before acting upon any information contained herein.",
  "While every reasonable effort has been made to ensure the accuracy and currency of the information published on this website, Harshavardhan Prakash Patil and Nexus Legal make no representations or warranties regarding its completeness, accuracy, reliability, or suitability.",
  "The Advocate shall not be liable for any direct, indirect, incidental, consequential, or other loss or damage arising from reliance upon the information available on this website.",
  "This website may contain references or links to third-party websites solely for the convenience of users. Such references do not constitute an endorsement, recommendation, or approval of the content of those websites. The Advocate assumes no responsibility for the content or privacy practices of any third-party website.",
  "Every effort has been made to ensure that this website is secure and free from malicious software. However, the Advocate does not warrant that the website will always remain uninterrupted, error-free, or free from viruses or other harmful components.",
  "All text, content, designs, graphics, logos, photographs, articles, and other material available on this website are the intellectual property of Harshavardhan Prakash Patil / Nexus Legal, unless otherwise stated, and may not be copied, reproduced, distributed, or used without prior written permission.",
  "By accessing this website, the user confirms that he/she has read, understood, and voluntarily accepted this Disclaimer, as well as the Privacy Policy and Terms of Use.",
];

export default function DisclaimerModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem(STORAGE_KEY);
    if (!accepted) setOpen(true);
    if (!accepted) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const agree = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    document.body.style.overflow = "";
    setOpen(false);
  };

  const disagree = () => {
    document.body.style.overflow = "";
    try { window.close(); } catch (e) {}
    window.location.href = "https://www.google.com";
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 md:p-8"
          data-testid="disclaimer-modal"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/85 backdrop-blur-2xl" />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-3xl max-h-[92vh] bg-[#0B0B0B] border border-[#C8A45D]/25 shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Gold hairline top */}
            <div className="h-px w-full bg-gradient-to-r from-transparent via-[#C8A45D]/70 to-transparent" />

            {/* Header */}
            <div className="px-6 md:px-12 pt-10 md:pt-12 pb-6 text-center">
              <ShieldCheck className="mx-auto text-[#C8A45D]" strokeWidth={1.1} size={30} />
              <div className="font-cinzel text-[10px] md:text-[11px] tracking-[0.4em] text-[#C8A45D] mt-5">
                DISCLAIMER · USER ACKNOWLEDGEMENT
              </div>
              <h2 className="mt-6 font-serif-display text-3xl md:text-5xl leading-[1.05] text-white/95">
                Welcome to the chamber of
              </h2>
              <div className="mt-4 font-serif-display text-2xl md:text-3xl italic text-[#C8A45D]/95">
                Harshavardhan Prakash Patil
              </div>
              <div className="mt-2 font-cinzel text-[9px] md:text-[10px] tracking-[0.35em] text-white/60">
                ADVOCATE · BOMBAY HIGH COURT · NEXUS LEGAL
              </div>
              <div className="mt-8 h-px w-16 mx-auto bg-[#C8A45D]/50" />
            </div>

            {/* Body — scrollable */}
            <div className="flex-1 overflow-y-auto px-6 md:px-12 pb-8 nx-disclaimer-scroll">
              <p className="font-body text-white/70 text-sm md:text-[15px] leading-relaxed mb-5">
                The Rules of the <span className="text-white">Bar Council of India</span> prohibit advocates and law firms from soliciting work or advertising in any manner.
              </p>
              <p className="font-body text-white/60 text-sm leading-relaxed mb-6">
                By clicking <span className="text-[#C8A45D] font-medium">&quot;I AGREE&quot;</span>, the user acknowledges and confirms that:
              </p>

              <ol className="space-y-4 mb-8">
                {ACKNOWLEDGEMENTS.map((t, i) => (
                  <li key={i} className="flex gap-4 items-start" data-testid={`disclaimer-item-${i}`}>
                    <span className="font-cinzel text-[10px] tracking-[0.25em] text-[#C8A45D] shrink-0 pt-1">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-white/60 text-sm leading-relaxed font-body">{t}</span>
                  </li>
                ))}
              </ol>

              <div className="mt-10 border border-[#C8A45D]/20 p-6 md:p-8">
                <div className="font-cinzel text-[10px] tracking-[0.35em] text-[#C8A45D] mb-3">IMPORTANT NOTICE</div>
                <p className="text-white/65 text-sm leading-relaxed font-body">
                  This website is not intended to advertise, solicit, or invite clients. The information made available herein is solely at the specific request of users seeking information about the Advocate and his professional practice. Any access to this website is entirely voluntary and does not create any contractual or professional relationship.
                </p>
              </div>
            </div>

            {/* Footer buttons */}
            <div className="border-t border-white/10 bg-[#0B0B0B]">
              <div className="px-6 md:px-12 py-5 md:py-6 flex flex-col-reverse md:flex-row gap-3 md:gap-4 md:justify-end">
                <button
                  onClick={disagree}
                  className="btn-ghost justify-center"
                  data-testid="disclaimer-disagree"
                >
                  <X size={14} /> I Disagree · Exit
                </button>
                <button
                  onClick={agree}
                  className="btn-primary justify-center"
                  data-testid="disclaimer-agree"
                >
                  I Agree · Enter Website
                </button>
              </div>
            </div>

            {/* Gold hairline bottom */}
            <div className="h-px w-full bg-gradient-to-r from-transparent via-[#C8A45D]/70 to-transparent" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
